// Accordion labels switched to sentence case (acronyms kept).
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  AccessGrant,
  AccessScope as ApiAccessScope,
  SortOrder,
} from '@ariksa/inventory-core';
import { Accordion, Box, HStack, Spinner, Stack } from '@chakra-ui/react';
import { Dict } from 'types/utils';

import { StackedCell, WithResourceIcon } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { IconTypes } from 'components/Icons';
import {
  AccessScopeButtons,
  AccountCell,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/AccessScope';
import { MetaAccordionItem } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetaAccordionItem';
import { useRedirectToGraph } from 'containers/ActiveCloudResource/hooks/useRedirectToGraph';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { useResourceType } from 'containers/App/hooks/useResourceType';

import { GrantCell } from './GrantCell';
import { GrantRows } from './GrantRows';
import { GrantsToggle } from './GrantsToggle';
import { PrincipalsToolbar } from './PrincipalsToolbar';
import { useOpenLinkedResource } from './useOpenLinkedResource';
import { usePrincipalsPage } from './usePrincipalsPage';
import {
  CELL_PADDING_Y,
  getPrivilegeOptions,
  getScopeItems,
  getTypeOptions,
  hasManyGrants,
  NARROW_COLUMN,
  PANEL_HEIGHT,
  renderHighPrivilegesCell,
  toApiScope,
} from './utils';

/**
 * The Principals tab of every non-Snowflake resource, one page at a time from
 * the paginated access-by-identity API. Search, filters, sort and paging all
 * go to the server, so a resource with thousands of principals loads its first
 * page as quickly as a small one.
 */
export const Principals: FC = () => {
  const {
    page,
    filters,
    update,
    setPageInfo,
    setSearchInput,
    applySearch,
    showRole,
  } = usePrincipalsPage();
  const { openResource, isOpening } = useOpenLinkedResource();
  const { getCloudNativeName, getCloudAgnosticName } = useResourceType();
  const { redirectToGraph } = useRedirectToGraph();

  const { data } = page;
  const items = useMemo(() => data?.items ?? [], [data]);
  const scopeTotal = data?.scope_counts?.[toApiScope(filters.scope)] ?? 0;
  const hasExternal =
    (data?.scope_counts?.[ApiAccessScope.OtherAccounts] ?? 0) > 0;

  // Which principal with several grants is expanded, by uuid.
  const [expandedUuid, setExpandedUuid] = useState<string | null>(null);
  useEffect(() => {
    setExpandedUuid(null); // avoid a stale expansion after another page loads
  }, [items]);

  // Opens a principal in the drawer, on its summary.
  const openPrincipal = (row: Dict<any>) =>
    openResource({
      uuid: row?.uuid,
      name: row?.name,
      account: row?.account,
      resourceType: row?.native_class,
    });

  const toggleRow = (row: Dict<any>) => {
    if (!hasManyGrants(row)) return;
    setExpandedUuid(prev => (prev === row.uuid ? null : row.uuid));
  };

  // A listed role is searched for here; any other role opens in the drawer.
  const onClickRole = (grant: AccessGrant) => {
    if (grant.listed && grant.name) showRole(grant.name);
    else if (grant.uuid)
      openResource({
        uuid: grant.uuid,
        name: grant.name,
        account: grant.account,
      });
  };
  const isRoleLoading = (grant: AccessGrant) => isOpening(grant.uuid);

  const typeOptions = useMemo(
    () =>
      getTypeOptions(
        data?.type_counts,
        scopeTotal,
        filters.principalType,
        getCloudAgnosticName,
      ),
    [data, scopeTotal, filters.principalType, getCloudAgnosticName],
  );
  const privilegeOptions = useMemo(
    () =>
      getPrivilegeOptions(
        data?.privilege_counts,
        scopeTotal,
        filters.privilege,
      ),
    [data, scopeTotal, filters.privilege],
  );

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Principal</Box>,
      accessor: 'name',
      sortKey: 'name',
      render: ({ row, value }) => (
        <HStack spacing={2} justify="flex-start">
          <Box flexShrink={0} minW={5}>
            {hasManyGrants(row) && (
              <GrantsToggle
                isOpen={row?.uuid === expandedUuid}
                onClick={() => toggleRow(row)}
              />
            )}
          </Box>
          <WithResourceIcon
            resourceType={row?.native_class}
            hStackStyles={{ flex: 1, minW: 0 }}
          >
            <StackedCell
              upper={value ?? '-'}
              lower={
                getCloudNativeName(row?.native_class) +
                ' / ' +
                getCloudAgnosticName(row?.agnostic_name)
              }
            />
          </WithResourceIcon>
          {/* The principal's drawer is loading after a click on its row. */}
          {isOpening(row?.uuid) && (
            <Spinner size="xs" color="primary" flexShrink={0} />
          )}
        </HStack>
      ),
    },
    {
      header: 'Mechanism',
      align: 'left',
      render: ({ row }) => (
        <GrantCell
          row={row}
          onClickRole={onClickRole}
          isRoleLoading={isRoleLoading}
        />
      ),
    },
    {
      header: 'Account',
      accessor: 'account',
      align: 'left',
      styles: NARROW_COLUMN,
      render: ({ row, value }) => (
        <AccountCell accountId={value} external={row?.external} />
      ),
    },
    {
      header: 'High Privileges',
      accessor: 'crud_level_counts_high',
      // The header stays on one line, so it sets this column's width.
      styles: NARROW_COLUMN,
      render: renderHighPrivilegesCell,
    },
    {
      header: '# Permissions',
      accessor: 'total_permission_count',
      styles: NARROW_COLUMN,
    },
  ];

  // The table calls this from an effect that re-runs whenever the function
  // changes, so it must stay the same across renders.
  const onSort = useCallback(
    ({ sortField, sortOrder }) => {
      if (sortField === 'name' && sortOrder)
        update({ sortOrder: sortOrder as SortOrder });
    },
    [update],
  );

  // Rendered by the table's `expanded` branch directly below the expanded row.
  const renderGrantRows = ({ row }) =>
    hasManyGrants(row) && row.uuid === expandedUuid ? (
      <GrantRows
        rowKey={row.uuid}
        grants={row.grants}
        onClickRole={onClickRole}
        isRoleLoading={isRoleLoading}
      />
    ) : null;

  const panel = (
    <Stack h="full" w="full" spacing={3}>
      <PrincipalsToolbar
        search={filters.searchInput}
        onSearchChange={setSearchInput}
        onSearch={applySearch}
        typeOptions={typeOptions}
        principalType={filters.principalType}
        onTypeChange={principalType => update({ principalType })}
        privilegeOptions={privilegeOptions}
        privilege={filters.privilege}
        onPrivilegeChange={privilege => update({ privilege })}
      />
      <Box flex={1} minH={0}>
        <Table
          columns={columns}
          data={items}
          noDataMessage="No principals found"
          onSort={onSort}
          sortBy={{ sortField: 'name', sortOrder: filters.sortOrder }}
          expanded
          isLoading={page.isLoading}
          subComponent={renderGrantRows}
          // A click on a row opens the principal; its chevron expands it.
          onRowClick={openPrincipal}
          cursor="pointer"
          pagination={{
            totalCount: data?.total ?? 0,
            pageInfo: filters.pageInfo,
            onChange: setPageInfo,
          }}
          // Keep the expanded parent row on the default surface — no active-row tint.
          styles={{
            // Shorter rows, so more fit in the panel.
            cell: { paddingTop: CELL_PADDING_Y, paddingBottom: CELL_PADDING_Y },
            row: {
              bg: 'white',
              css: {
                'td:first-of-type': {
                  position: 'sticky',
                  left: 0,
                  overflow: 'hidden',
                  background: 'white',
                },
              },
            },
          }}
        />
      </Box>
    </Stack>
  );

  // One list for both accounts: the account is a column rather than a heading.
  return (
    <Accordion defaultIndex={[0]} allowMultiple>
      <MetaAccordionItem
        iconType={IconTypes.Identity}
        label={'Principals that can access this resource'}
        onClickDownload={() => {}}
        rightHeader={() =>
          hasExternal ? (
            <AccessScopeButtons
              items={getScopeItems(data?.scope_counts)}
              scope={filters.scope}
              onChange={scope => update({ scope })}
            />
          ) : null
        }
        panel={panel}
        // Taller than the default, with less space above the search row (6px
        // instead of 8px) and under the pagination bar (8px instead of 20px).
        height={PANEL_HEIGHT}
        styles={{ panel: { pt: '6px', pb: 2 } }}
        iconBGColor="primary"
        isLoading={page.isLoading}
        totalCount={scopeTotal}
        onShowGraph={() => redirectToGraph('identity', MetadataTabs.Principal)}
      />
    </Accordion>
  );
};
